import { createContext, useContext } from 'react';

import type { StorefrontLanguage } from '@/features/storefront/api/storefront';
import type { TextDirection } from '@/lib/i18n/languages';

/**
 * The language a storefront is being rendered in.
 *
 * Separate from the dashboard's `I18nProvider` on purpose: a merchant reading
 * the dashboard in English can preview their shop in Tamil, and the two must
 * not share one setting. Storefront copy (`useContent` / `useShopContent`)
 * reads this; the dashboard's `useT()` does not.
 */
export interface StorefrontLanguageContextValue {
  /** The language on screen - the previous one while a new one loads. */
  code: string;
  /** Its `Intl` locale: `hi-IN`. */
  locale: string;
  direction: TextDirection;
  /** The shop's default language (unprefixed URLs). */
  defaultCode: string;
  /** Enabled languages, in switcher order. Empty on a single-language shop. */
  languages: readonly StorefrontLanguage[];
  /** Ask for another language. Whoever owns the URL decides how. */
  setLanguage: (code: string) => void;
  /** False while the requested language's built-in copy is still loading. */
  ready: boolean;
  /**
   * The store's `i18n.switcher_enabled`. The theme's `language_switcher` still
   * decides where (and whether) a switcher is drawn; this is the store saying
   * shoppers may switch at all.
   */
  switcherEnabled: boolean;
}

/** English, no switcher - what a storefront renders with no provider. */
export const DEFAULT_STOREFRONT_LANGUAGE: StorefrontLanguageContextValue = {
  code: 'en',
  locale: 'en-IN',
  direction: 'ltr',
  defaultCode: 'en',
  languages: [],
  setLanguage: () => {},
  ready: true,
  switcherEnabled: false,
};

export const StorefrontLanguageContext = createContext<StorefrontLanguageContextValue | null>(null);

export function useStorefrontLanguage(): StorefrontLanguageContextValue {
  return useContext(StorefrontLanguageContext) ?? DEFAULT_STOREFRONT_LANGUAGE;
}
